import type { ReactElement } from 'react'
import { createContext, use, useContext, useDeferredValue } from 'react'
import once from 'lodash/once'

import { type RouterComponentBundleFetchData } from '@app/packages/routing/types'

export type PolygonMode = 'dev' | 'user'

export const ModeContext = createContext<PolygonMode>('user')

export const useMode = (): PolygonMode => useContext(ModeContext)

export class RichLabel {
  public readonly label: string
  public readonly localizedLabel?: string
  public readonly help?: string
  constructor(args: { label: string; localizedLabel?: string; help?: string }) {
    this.label = args.label
    this.localizedLabel = args.localizedLabel
    this.help = args.help
  }
}

export type Label = string | RichLabel

const MUTATE = '@POLYGON/MUTATE'

type MUTATE_ACTION = {
  type: typeof MUTATE
  payload: (state: any) => any
}

/** The path, not a koa context: the caller has one, and this only ever asked about the path. */
export const isPolygonRequest = ({ path }: { path: string }) => {
  if (API_MODE === 'production') return false
  return path.startsWith('/__polygon')
}

export function reducerWithMutator<R extends (state: any, action: any) => any>(reducer: R): R {
  return ((state, action) => {
    try {
      if (action.type === MUTATE) return action.payload(state)
      return reducer(state, action)
    } catch {
      return reducer(state, action)
    }
  }) as R
}

export const polygonMutate = <State extends any>(mutator: (state: State) => State): MUTATE_ACTION => ({
  type: MUTATE,
  payload: mutator,
})

export const createPolygonComponent = (
  Component: (() => ReactElement | null) & { fetchData?: RouterComponentBundleFetchData },
  { fetchData }: { fetchData?: RouterComponentBundleFetchData } = {}
) => {
  Component.fetchData = fetchData
  return Component
}

const ui = once(() => import('./ui'))

export const useHelpers = () => {
  const uil = useDeferredValue(use(ui()))
  return uil.helpers
}
